import { useEffect, useRef } from 'react';
import { getSupabaseRealtimeClient } from '../services/supabaseRealtimeClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UseHybridShelfIndexSyncParams {
  enabled?: boolean;
  onSync: (source: 'realtime' | 'poll' | 'resume') => void | Promise<void>;
  /** Polling interval in ms. Set to 0 to disable polling (realtime only). */
  pollMs?: number;
  tables?: string[];
  schema?: string;
}

export function useHybridShelfIndexSync({
  enabled = true,
  onSync,
  pollMs = 20000,
  tables = ['shelf_indexes'],
  schema = 'wms',
}: UseHybridShelfIndexSyncParams): void {
  const onSyncRef = useRef(onSync);
  const lastSyncAtRef = useRef(0);
  const tablesKey = Array.isArray(tables) ? tables.join('|') : '';

  useEffect(() => {
    onSyncRef.current = onSync;
  }, [onSync]);

  useEffect(() => {
    if (!enabled || typeof onSyncRef.current !== 'function') {
      return undefined;
    }

    const triggerSync = (source: 'realtime' | 'poll' | 'resume') => {
      const now = Date.now();
      if ((source === 'realtime' || source === 'resume') && now - lastSyncAtRef.current < 250) {
        return;
      }
      lastSyncAtRef.current = now;
      Promise.resolve(onSyncRef.current(source)).catch((error: unknown) => {
        console.error(`[hybrid-sync] ${source} sync failed:`, error);
      });
    };

    const onVisibilityChange = (): void => {
      if (!document.hidden) {
        triggerSync('resume');
      }
    };
    const onOnline = (): void => {
      triggerSync('resume');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    const pollTimer = pollMs > 0
      ? window.setInterval(() => {
          if (document.hidden) {
            return;
          }
          triggerSync('poll');
        }, pollMs)
      : null;

    const realtimeClient = getSupabaseRealtimeClient();
    let channel: RealtimeChannel | null = null;

    if (realtimeClient) {
      channel = realtimeClient.channel(`shelf-index-sync-${Math.random().toString(36).slice(2)}`);

      const subscribedTables = Array.isArray(tables) && tables.length > 0 ? tables : ['shelf_indexes'];
      subscribedTables.forEach((table) => {
        channel!.on(
          'postgres_changes',
          { event: '*', schema, table },
          () => triggerSync('realtime')
        );
      });

      channel.subscribe((status: string) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('[hybrid-sync] Realtime channel error, polling fallback active.');
        }
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      if (pollTimer != null) {
        window.clearInterval(pollTimer);
      }
      if (realtimeClient && channel) {
        realtimeClient.removeChannel(channel).catch(() => {
          // no-op cleanup
        });
      }
    };
  }, [enabled, pollMs, schema, tables, tablesKey]);
}
